/**
 * PALI BEAUTY x FAE STYLE SCROLL & INTERACTION ENGINE
 * Powered by GSAP, ScrollTrigger, and Lenis Smooth Scroll
 */

(function () {
  'use strict';

  // 1. DYNAMIC DEPENDENCY LOADER
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  async function initAnimationLibraries() {
    try {
      if (!window.gsap) {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js');
      }
      if (!window.ScrollTrigger) {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js');
      }
      if (!window.Lenis) {
        await loadScript('https://unpkg.com/@studio-freight/lenis@1.0.32/dist/lenis.min.js');
      }

      initAllAnimations();
    } catch (e) {
      console.warn('Animation scripts failed to load gracefully:', e);
      // Fallback: Initialize basic carousels and controls without GSAP
      initFallbackCarousels();
    }
  }

  function initAllAnimations() {
    // 2. LENIS SMOOTH SCROLL
    let lenis;
    if (window.Lenis) {
      try {
        lenis = new Lenis({
          duration: 1.2,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          smooth: true,
          smoothTouch: false,
        });

        function raf(time) {
          lenis.raf(time);
          requestAnimationFrame(raf);
        }
        requestAnimationFrame(raf);

        if (window.gsap && window.ScrollTrigger) {
          lenis.on('scroll', ScrollTrigger.update);
          gsap.ticker.add((time) => {
            lenis.raf(time * 1000);
          });
          gsap.ticker.lagSmoothing(0);
        }
      } catch (err) {
        console.warn('Lenis init note:', err);
      }
    }

    if (!window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    // 3. WORD COLOR REVEAL ON SCROLL
    document.querySelectorAll('.pali-scroll-text-reveal__heading').forEach((heading) => {
      const highlightColor = heading.dataset.highlightColor || '#B6E454';
      const finalColor = heading.dataset.textColor || '#000000';
      const words = heading.textContent.trim().split(/\s+/);
      heading.innerHTML = words.map((w) => `<span>${w}</span>`).join(' ');

      gsap.fromTo(
        heading.querySelectorAll('span'),
        { color: highlightColor, opacity: 0.4 },
        {
          color: finalColor,
          opacity: 1,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: heading,
            start: 'top 85%',
            end: 'bottom 25%',
            scrub: 0.6,
          },
        }
      );
    });

    // 4. GSAP MATCH MEDIA (DESKTOP / MOBILE)
    const mm = gsap.matchMedia();

    // DESKTOP ANIMATIONS (> 768px)
    mm.add('(min-width: 768px)', () => {
      // Hero video scaling & text motion
      document.querySelectorAll('.pali-about-hero').forEach((hero) => {
        const media = hero.querySelector('.pali-about-hero__media-wrapper');
        if (media) {
          gsap.timeline({
            scrollTrigger: {
              trigger: hero,
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            },
          }).to(media, { scale: 0.65, borderRadius: '24px', ease: 'power2.out' });
        }

        const textLeft = hero.querySelector('.pali-about-hero__text-item--top-left');
        if (textLeft) {
          gsap.timeline({
            scrollTrigger: {
              trigger: hero,
              start: 'top 15%',
              end: 'bottom 20%',
              scrub: true,
            },
          })
            .fromTo(textLeft, { x: -250, opacity: 0 }, { x: 0, opacity: 1 })
            .to(textLeft, { x: 0 });
        }

        const textRight = hero.querySelector('.pali-about-hero__text-item--top-right');
        if (textRight) {
          gsap.timeline({
            scrollTrigger: {
              trigger: hero,
              start: 'top 50%',
              end: 'bottom 20%',
              scrub: true,
            },
          })
            .fromTo(textRight, { x: 250, opacity: 0 }, { x: 0, opacity: 1 })
            .to(textRight, { x: 0 });
        }
      });

      // Split Collage - Letter Convergence
      document.querySelectorAll('.pali-split-collage').forEach((collage) => {
        const firstLetter = collage.querySelector('.pali-letter-1');
        const secondLetter = collage.querySelector('.pali-letter-2');
        const thirdLetter = collage.querySelector('.pali-letter-3');
        const otherLetters = collage.querySelectorAll('.pali-other-letters');

        if (firstLetter) {
          gsap.timeline({
            scrollTrigger: {
              trigger: collage,
              start: 'top 90%',
              end: 'top 15%',
              scrub: true,
            },
          })
            .fromTo(firstLetter, { x: 350 }, { x: 350, duration: 0.5 })
            .to(firstLetter, { x: 0, duration: 0.5 });
        }

        if (secondLetter) {
          gsap.timeline({
            scrollTrigger: {
              trigger: collage,
              start: 'top 60%',
              end: 'top 15%',
              scrub: true,
            },
          })
            .fromTo(secondLetter, { x: 140 }, { x: 140, duration: 0.5 })
            .to(secondLetter, { x: 0, duration: 0.5 });
        }

        if (thirdLetter) {
          gsap.timeline({
            scrollTrigger: {
              trigger: collage,
              start: 'top 60%',
              end: 'top 15%',
              scrub: true,
            },
          })
            .fromTo(thirdLetter, { x: -140 }, { x: -140, duration: 0.5 })
            .to(thirdLetter, { x: 0, duration: 0.5 });
        }

        if (otherLetters.length) {
          gsap.fromTo(
            otherLetters,
            { opacity: 0 },
            {
              opacity: 1,
              stagger: 0.05,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: collage,
                start: 'top 30%',
                end: 'top 10%',
                scrub: true,
              },
            }
          );
        }

        // Floating Parallax Images
        collage.querySelectorAll('.pali-split-collage__floating-img').forEach((img, idx) => {
          const speed = parseFloat(img.dataset.speed || 1.5) * (idx % 2 === 0 ? 1 : -1);
          gsap.to(img, {
            y: `${speed * 120}px`,
            ease: 'none',
            scrollTrigger: {
              trigger: collage,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          });
        });
      });
    });

    // MOBILE ANIMATIONS (< 768px)
    mm.add('(max-width: 767px)', () => {
      document.querySelectorAll('.pali-about-hero').forEach((hero) => {
        const media = hero.querySelector('.pali-about-hero__media-wrapper');
        if (media) {
          gsap.timeline({
            scrollTrigger: {
              trigger: hero,
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            },
          }).to(media, { scale: 0.8, borderRadius: '16px', ease: 'power2.out' });
        }
      });
    });

    // 5. VIDEO AUDIO TOGGLE & AUTO-MUTE
    setupVideoAudioControls();

    // 6. STORY SLIDER / CAROUSELS
    initStorySliders();

    // 7. PRODUCT CAROUSEL INTERACTION
    initProductCarousels();
  }

  function setupVideoAudioControls() {
    document.querySelectorAll('.pali-about-hero').forEach((hero) => {
      const video = hero.querySelector('video');
      const muteBtn = hero.querySelector('.pali-about-hero__mute-btn');
      if (!video || !muteBtn) return;

      const muteIcon = muteBtn.querySelector('.pali-about-hero__mute-icon');
      const unmuteIcon = muteBtn.querySelector('.pali-about-hero__unmute-icon');

      muteBtn.addEventListener('click', () => {
        if (video.muted) {
          video.muted = false;
          video.volume = 1;
          if (muteIcon) muteIcon.style.display = 'none';
          if (unmuteIcon) unmuteIcon.style.display = 'block';
        } else {
          video.muted = true;
          if (muteIcon) muteIcon.style.display = 'block';
          if (unmuteIcon) unmuteIcon.style.display = 'none';
        }
      });

      // Auto-mute when out of viewport
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && !video.muted) {
              video.muted = true;
              if (muteIcon) muteIcon.style.display = 'block';
              if (unmuteIcon) unmuteIcon.style.display = 'none';
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(video);
    });
  }

  function initStorySliders() {
    document.querySelectorAll('.pali-story-slider').forEach((slider) => {
      const track = slider.querySelector('.pali-story-slider__track');
      const slides = slider.querySelectorAll('.pali-story-slider__slide');
      const prevBtn = slider.querySelector('.pali-story-slider__btn--prev');
      const nextBtn = slider.querySelector('.pali-story-slider__btn--next');
      if (!track || slides.length <= 1) return;

      let currentIdx = 0;
      const total = slides.length;
      const autoPlayMs = parseInt(slider.dataset.autoplay || '0', 10);
      let autoTimer = null;

      function goTo(idx) {
        currentIdx = (idx + total) % total;
        track.style.transform = `translateX(-${currentIdx * 100}%)`;
      }

      if (prevBtn) prevBtn.addEventListener('click', () => { goTo(currentIdx - 1); resetTimer(); });
      if (nextBtn) nextBtn.addEventListener('click', () => { goTo(currentIdx + 1); resetTimer(); });

      function resetTimer() {
        if (autoPlayMs > 0) {
          clearInterval(autoTimer);
          autoTimer = setInterval(() => goTo(currentIdx + 1), autoPlayMs);
        }
      }

      resetTimer();
    });
  }

  function initProductCarousels() {
    document.querySelectorAll('.pali-product-carousel').forEach((section) => {
      const scroller = section.querySelector('.pali-product-carousel__scroller');
      const prevBtn = section.querySelector('.pali-product-carousel__nav-btn--prev');
      const nextBtn = section.querySelector('.pali-product-carousel__nav-btn--next');
      const progressBar = section.querySelector('.pali-product-carousel__progress-bar');
      if (!scroller) return;

      const card = scroller.querySelector('.pali-product-card');
      const cardWidth = card ? card.offsetWidth + 24 : 300;

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          scroller.scrollBy({ left: -cardWidth, behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          scroller.scrollBy({ left: cardWidth, behavior: 'smooth' });
        });
      }

      if (progressBar) {
        scroller.addEventListener('scroll', () => {
          const maxScroll = scroller.scrollWidth - scroller.clientWidth;
          if (maxScroll > 0) {
            const pct = (scroller.scrollLeft / maxScroll) * 75; // leave room for bar width
            progressBar.style.transform = `translateX(${(pct / 75) * (scroller.clientWidth - progressBar.offsetWidth)}px)`;
          }
        }, { passive: true });
      }
    });
  }

  function initFallbackCarousels() {
    initStorySliders();
    initProductCarousels();
    setupVideoAudioControls();
  }

  // BOOTSTRAP
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAnimationLibraries);
  } else {
    initAnimationLibraries();
  }

  // Re-init on Shopify theme editor section reload
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initAnimationLibraries);
  }
})();
